import React, { useEffect, useState } from "react";
import {
  User,
  Search,
  ChevronLeft,
  ChevronRight,
  Package,
} from "react-feather";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

function ACustomers() {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem("AdminEcomme"));
  const [allUsers, setAllUsers] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allAddress, setAllAddress] = useState([]);
  const [allCommands, setAllCommands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'cards'
  const itemsPerPage = 10;

  if (admin) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${admin.token}`;
  }

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, profilesRes, addressesRes, commandsRes] =
        await Promise.all([
          axios.get(`${BackendUrl}/api/user/getUsers`),
          axios.get(`${BackendUrl}/api/profilesRoutes/all`),
          axios.get(`${BackendUrl}/api/shippingAddressRoutes/all`),
          axios.get(`${BackendUrl}/api/ordersRoutes/`),
        ]);
      setAllUsers(usersRes.data?.user);
      setAllProfiles(profilesRes.data.data);
      setAllAddress(addressesRes.data.data);
      setAllCommands(commandsRes.data.commandes);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredUsers = allUsers?.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedUsers = filteredUsers?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (
      direction === "next" &&
      currentPage < Math.ceil(filteredUsers?.length / itemsPerPage)
    ) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleUserClick = (id) => {
    navigate(`/Admin/ACustomerDet/${id}`);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "table" ? "cards" : "table");
  };

  // Spinner component for loading state
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
      <div
        className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
        style={{ borderColor: COLORS.orange }}
      ></div>
    </div>
  );

  // Card view for users
  const UserCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {displayedUsers?.map((user) => {
        const profile = allProfiles.find((prof) => prof.clefUser === user._id);
        const address = allAddress.find((addr) => addr.clefUser === user._id);
        const commandCount = allCommands.filter(
          (cmd) => cmd.clefUser === user._id
        ).length;

        return (
          <div
            key={user._id}
            className="rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
            style={{
              backgroundColor: COLORS.white,
              borderTop: `3px solid ${COLORS.orange}`,
            }}
            onClick={() => handleUserClick(user._id)}
          >
            <div className="p-4">
              <div className="flex items-center space-x-4">
                <div
                  className="rounded-full p-2"
                  style={{ backgroundColor: COLORS.paleGreen }}
                >
                  {profile?.image ? (
                    <img
                      src={profile.image}
                      alt="User"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <User size={32} style={{ color: COLORS.green }} />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-lg">
                    {address?.name || user.name || "Sans nom"}
                  </h3>
                  <p className="text-sm" style={{ color: COLORS.gray }}>
                    {user.email}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div
                  className="p-2 rounded"
                  style={{ backgroundColor: COLORS.lightGreen }}
                >
                  <p
                    className="text-xs font-semibold"
                    style={{ color: COLORS.darkGreen }}
                  >
                    Téléphone
                  </p>
                  <p>{profile?.numero || user.phoneNumber || "Non spécifié"}</p>
                </div>
                <div
                  className="p-2 rounded flex items-center justify-between"
                  style={{ backgroundColor: COLORS.lightOrange }}
                >
                  <div>
                    <p
                      className="text-xs font-semibold"
                      style={{ color: COLORS.orange }}
                    >
                      Commandes
                    </p>
                    <p className="font-bold">{commandCount}</p>
                  </div>
                  <Package size={20} style={{ color: COLORS.orange }} />
                </div>
              </div>

              <div
                className="mt-3 pt-3 border-t text-xs text-gray-500"
                style={{ borderColor: COLORS.lightGray }}
              >
                <p className="truncate">ID: {user._id}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className="ACustomers p-4 md:p-6"
      style={{ backgroundColor: COLORS.paleGreen }}
    >
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 space-y-4 md:space-y-0">
          <h3 className="text-2xl font-bold" style={{ color: COLORS.green }}>
            Clients
          </h3>

          <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <div className="relative w-full md:w-64">
              <input
                type="search"
                placeholder="Rechercher des clients..."
                className="border rounded-full py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: COLORS.lightGray,
                  focusRing: COLORS.orange,
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} style={{ color: COLORS.gray }} />
              </div>
            </div>

            <button
              onClick={toggleViewMode}
              className="px-4 py-2 rounded-md font-medium transition-colors duration-200 text-white w-full md:w-auto"
              style={{ backgroundColor: COLORS.green }}
            >
              {viewMode === "table" ? "Vue Cartes" : "Vue Tableau"}
            </button>
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {viewMode === "table" ? (
              <div
                className="overflow-x-auto rounded-lg border"
                style={{ borderColor: COLORS.lightGray }}
              >
                <table
                  className="min-w-full divide-y"
                  style={{ divideColor: COLORS.lightGray }}
                >
                  <thead>
                    <tr style={{ backgroundColor: COLORS.green }}>
                      <th className="px-4 py-3 text-left text-white font-medium">
                        Photo
                      </th>
                      <th className="px-4 py-3 text-left text-white font-medium">
                        Nom
                      </th>
                      <th className="px-4 py-3 text-left text-white font-medium hidden md:table-cell">
                        Identifiant
                      </th>
                      <th className="px-4 py-3 text-left text-white font-medium">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-white font-medium hidden md:table-cell">
                        Téléphone
                      </th>
                      <th className="px-4 py-3 text-left text-white font-medium">
                        Commandes
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className="bg-white divide-y"
                    style={{ divideColor: COLORS.lightGray }}
                  >
                    {displayedUsers?.map((user, index) => {
                      const profile = allProfiles.find(
                        (prof) => prof.clefUser === user._id
                      );
                      const address = allAddress.find(
                        (addr) => addr.clefUser === user._id
                      );
                      const commandCount = allCommands.filter(
                        (cmd) => cmd.clefUser === user._id
                      ).length;

                      const rowBackground =
                        index % 2 === 0 ? COLORS.white : COLORS.paleGreen;

                      return (
                        <tr
                          key={user._id}
                          className="hover:opacity-90 cursor-pointer transition-opacity"
                          style={{ backgroundColor: rowBackground }}
                          onClick={() => handleUserClick(user._id)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex-shrink-0 h-10 w-10">
                              {profile?.image ? (
                                <img
                                  src={profile.image}
                                  alt="User"
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <div
                                  className="h-10 w-10 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: COLORS.lightGreen }}
                                >
                                  <User
                                    size={20}
                                    style={{ color: COLORS.green }}
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="font-medium">
                              {address?.name || user.name || "Sans nom"}
                            </div>
                          </td>
                          <td
                            className="px-4 py-3 whitespace-nowrap hidden md:table-cell text-sm"
                            style={{ color: COLORS.gray }}
                          >
                            {user._id.substring(0, 8)}...
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm">{user.email}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                            <div className="text-sm">
                              {profile?.numero || user.phoneNumber || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full"
                              style={{
                                backgroundColor:
                                  commandCount > 0
                                    ? COLORS.lightOrange
                                    : COLORS.lightGray,
                                color:
                                  commandCount > 0
                                    ? COLORS.orange
                                    : COLORS.gray,
                              }}
                            >
                              {commandCount}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <UserCards />
            )}

            {/* Pagination controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
              <div className="text-sm" style={{ color: COLORS.gray }}>
                Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  filteredUsers?.length || 0
                )}{" "}
                sur {filteredUsers?.length || 0} clients
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange("prev")}
                  disabled={currentPage === 1}
                  className="flex items-center justify-center w-10 h-10 rounded-full disabled:opacity-40 transition-opacity"
                  style={{
                    backgroundColor:
                      currentPage === 1 ? COLORS.lightGray : COLORS.orange,
                    color: COLORS.white,
                  }}
                >
                  <ChevronLeft size={18} />
                </button>

                <div
                  className="flex items-center justify-center px-4 rounded-md font-medium"
                  style={{
                    backgroundColor: COLORS.white,
                    color: COLORS.green,
                    border: `1px solid ${COLORS.lightGray}`,
                  }}
                >
                  {currentPage} /{" "}
                  {Math.max(
                    1,
                    Math.ceil((filteredUsers?.length || 0) / itemsPerPage)
                  )}
                </div>

                <button
                  onClick={() => handlePageChange("next")}
                  disabled={
                    currentPage ===
                      Math.ceil((filteredUsers?.length || 0) / itemsPerPage) ||
                    !filteredUsers?.length
                  }
                  className="flex items-center justify-center w-10 h-10 rounded-full disabled:opacity-40 transition-opacity"
                  style={{
                    backgroundColor:
                      currentPage ===
                        Math.ceil(
                          (filteredUsers?.length || 0) / itemsPerPage
                        ) || !filteredUsers?.length
                        ? COLORS.lightGray
                        : COLORS.orange,
                    color: COLORS.white,
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ACustomers;
